import { motion } from "framer-motion";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import { useLocation, useRouteMatch } from "react-router-dom";

import { copy } from "shared/assets";
import { Menu, Plus, Minus } from "shared/assets/icons";
import { ActionDropdown } from "shared/components";
import { IMenuItemType, ISideMenuItem } from "shared/components/SideMenu/types";
import { slideInFromTop, VisuallyHiddenElement } from "shared/styles/constants";
import { isActiveItem } from "../../utils";

import {
  StyledMenuItem,
  MenuItemIcon,
  ActionIcon,
  MenuItemLink,
  MenuItemProps,
  MenuItemButton,
  MenuItemInner,
  OverflowMenu,
  ExpandIcon,
  MenuItemTitle,
} from "./styles";

interface SideMenuItemProps extends ISideMenuItem, MenuItemProps {
  iconTransform?: string;
  actionIconTransform?: string;
  innerRef?: any;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  expandedItemId: string | null;
  setExpandedItemId: (id: string | null) => void;
  setDragRef?: Dispatch<SetStateAction<Element | null | undefined>>;
  handleClose?: () => void;
}

export const SideMenuItem: React.FC<SideMenuItemProps> = ({
  path,
  matchPaths,
  icon,
  iconTransform = "none",
  actionIcon,
  actionIconTransform = "none",
  title,
  actions,
  type = IMenuItemType.default,
  onClick,
  handleClose = () => {},
  innerRef,
  dragHandleProps = {},
  setDragRef,
  subItems,
  accentColor,
  expandableSubItems,
  subItemIndex = 0,
  id,
  expandedItemId,
  setExpandedItemId,
  ...rest
}) => {
  const match = useRouteMatch(path || "");
  const location = useLocation();

  const isExpanded = useMemo(() => id === expandedItemId, [expandedItemId, id]);

  const isActive = useMemo(
    () => isActiveItem({ path, matchPaths, match, location }),
    [match, path, location, matchPaths]
  );

  const isMatchRef = useRef<boolean>();

  useEffect(() => {
    if (expandedItemId === id) {
      isMatchRef.current = true;
    }
  }, [expandedItemId, id]);

  // if a subitem is active, expand the item
  useEffect(() => {
    const isMatchSubItem = !!subItems?.find(({ path, matchPaths }) => {
      return (
        path === location.pathname || matchPaths?.includes(location.pathname)
      );
    });

    if (isMatchSubItem) {
      setExpandedItemId(id);
    }
  }, [id, location, setExpandedItemId, subItems]);

  useEffect(() => {
    if (expandableSubItems) {
      const isMatch = matchPaths?.includes(
        `${location.pathname}${location.search}${location.hash}`
      );
      if (isMatch && isMatch !== isMatchRef.current && expandedItemId !== id) {
        setExpandedItemId(id);
        isMatchRef.current = isMatch;
      }
    }
  }, [
    id,
    location,
    matchPaths,
    expandedItemId,
    expandableSubItems,
    setExpandedItemId,
  ]);

  const handleClick = () => {
    if (expandableSubItems) {
      setExpandedItemId(isExpanded ? null : id);
    } else {
      onClick && onClick();
    }
  };

  const renderMenuInner = () => (
    <MenuItemInner>
      {icon && (
        <span {...dragHandleProps} ref={setDragRef}>
          <MenuItemIcon as={icon} style={{ transform: iconTransform }} />
          {setDragRef && (
            <VisuallyHiddenElement>{copy.a11y.drag}</VisuallyHiddenElement>
          )}
        </span>
      )}
      <MenuItemTitle data-label>{title}</MenuItemTitle>
      {actionIcon && (
        <ActionIcon>
          <ActionIcon
            style={{ transform: actionIconTransform }}
            as={actionIcon}
          />
        </ActionIcon>
      )}
      {expandableSubItems && <ExpandIcon as={isExpanded ? Minus : Plus} />}
    </MenuItemInner>
  );

  const renderSubItems = () => (
    <motion.div {...slideInFromTop}>
      {subItems?.map(({ onClick, id, ...rest }) => (
        <SideMenuItem
          {...rest}
          key={id}
          id={id}
          onClick={() => {
            onClick && onClick();
            handleClose();
          }}
          handleClose={handleClose}
          subItemIndex={subItemIndex + 1}
          accentColor={accentColor}
          expandedItemId={expandedItemId}
          setExpandedItemId={setExpandedItemId}
        />
      ))}
    </motion.div>
  );

  return (
    <>
      <StyledMenuItem
        type={type}
        {...rest}
        ref={innerRef}
        isActive={isActive}
        accentColor={accentColor}
        subItemIndex={subItemIndex}
        data-testid={id}
      >
        {path && (
          <MenuItemLink to={path} onClick={handleClick} smooth>
            {renderMenuInner()}
          </MenuItemLink>
        )}
        {!path && (
          <MenuItemButton type="button" onClick={handleClick}>
            {renderMenuInner()}
          </MenuItemButton>
        )}
        {!!actions?.length && (
          <OverflowMenu>
            <ActionDropdown
              label={copy.menu.open}
              options={actions}
              triggerIcon={Menu}
              placement="bottom-end"
              wrapOptions={true}
            />
          </OverflowMenu>
        )}
      </StyledMenuItem>
      {(isActive || isExpanded) && renderSubItems()}
    </>
  );
};
